<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-support-cours-card">
    <div class="card-header">
      <h3 class="text-black fw-semibold">
        Modifier les collections du support
        <span v-if="support">: {{ support.titre }}</span>
      </h3>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <div class="row gx-3 mb-4" v-if="support">
        <div class="col-md-12">
          <div class="alert alert-info">
            <p class="mb-0">
              Modification des médias pour :
              <strong>{{ support.titre }}</strong>
              <br />
              Auteur : <strong>{{ support.auteur || 'N/A' }}</strong>
            </p>
          </div>
        </div>
      </div>

      <form @submit.prevent="updateSupportMedias">
        <!-- ===================== COLLECTION DE VIDÉOS ===================== -->
        <div class="border border-warning mb-10 mt-10">
          <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-warning">
            <div class="col-md-7">
              <h4 class="fs-4 text-white">
                <i class="fa fa-video me-2"></i> Collection de vidéos
              </h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button class="btn btn-light" type="button" @click="addVideo">
                  <i class="fa fa-plus-circle me-1 fs-12"></i>
                  Ajouter une vidéo
                </button>
              </div>
            </div>
          </div>
          <div class="p-3">
            <div v-if="videos.length === 0" class="text-center text-muted py-3">
              Aucune vidéo. Cliquez sur "Ajouter une vidéo" pour en ajouter.
            </div>
            <div v-for="(video, index) in videos" :key="video.tempId || index">
              <!-- En-têtes colonnes -->
              <div class="row mt-10" v-if="index === 0">
                <div class="col-md-3"><label class="d-block text-black fw-semibold mb-10">Titre <span class="text-danger">*</span></label></div>
                <div class="col-md-4"><label class="d-block text-black fw-semibold mb-10">URL / Fichier <span class="text-danger">*</span></label></div>
                <div class="col-md-4"><label class="d-block text-black fw-semibold mb-10">Description</label></div>
                <div class="col-md-1"><label class="d-block text-black fw-semibold mb-10">Action</label></div>
              </div>
              <div class="row mb-10 align-items-start" :class="{ 'opacity-50': video._toDelete }">
                <!-- Titre -->
                <div class="col-md-3">
                  <input type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Ex: Introduction au chapitre 1"
                    v-model="video.titre"
                    :disabled="video._toDelete" />
                </div>
                <!-- Source -->
                <div class="col-md-4">
                  <div class="d-flex gap-2 mb-1" v-if="!video._toDelete">
                    <div class="form-check">
                      <input class="form-check-input" type="radio" :name="`videoSource_${index}`"
                        value="url" v-model="video.sourceType" />
                      <label class="form-check-label">URL</label>
                    </div>
                    <div class="form-check ms-2">
                      <input class="form-check-input" type="radio" :name="`videoSource_${index}`"
                        value="fichier" v-model="video.sourceType" />
                      <label class="form-check-label">Fichier</label>
                    </div>
                  </div>
                  <input v-if="video.sourceType === 'url' && !video._toDelete"
                    type="url" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="https://youtube.com/..." v-model="video.url" />
                  <div v-else-if="video.sourceType === 'fichier' && !video._toDelete">
                    <input type="file" accept="video/*"
                      class="form-control shadow-none fs-md-15 text-black"
                      @change="(e) => onVideoFichierChange(index, e)" />
                    <small v-if="video.fichierName" class="text-success d-block mt-1">
                      {{ video.fichierName }}
                    </small>
                    <!-- Lien actuel si média existant -->
                    <small v-if="video.id && video.lienActuel && !video.fichier" class="text-muted d-block mt-1">
                      Fichier actuel : {{ video.lienActuel }}
                    </small>
                  </div>
                  <span v-if="video._toDelete" class="badge bg-danger">Sera supprimé</span>
                </div>
                <!-- Description -->
                <div class="col-md-4">
                  <textarea class="form-control shadow-none fs-md-15 text-black" rows="2"
                    placeholder="Description courte (optionnel)"
                    v-model="video.description"
                    :disabled="video._toDelete"></textarea>
                </div>
                
                <!-- Actions -->
                <div class="col-md-1 pt-1 d-flex justify-content-center align-items-center">
                  <button v-if="video.id" class="btn btn-sm border-0 w-100"
                    :class="video._toDelete ? 'btn-secondary' : 'btn-warning'"
                    type="button" @click="toggleDeleteVideo(index)"
                    :title="video._toDelete ? 'Annuler la suppression' : 'Marquer pour suppression'">
                    <i :class="video._toDelete ? 'fas fa-undo' : 'fas fa-trash'"></i>
                  </button>
                  <button v-else class="btn btn-sm btn-danger border-0 w-100"
                    type="button" @click="removeVideo(index)">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
              <hr v-if="index < videos.length - 1" class="my-2" />
            </div>
          </div>
        </div>

        <!-- ===================== COLLECTION DE TEXTES ===================== -->
        <div class="border border-info mb-10 mt-10">
          <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-info">
            <div class="col-md-7">
              <h4 class="fs-4 text-white">
                <i class="fa fa-file-alt me-2"></i> Collection de textes
              </h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button class="btn btn-light" type="button" @click="addTexte">
                  <i class="fa fa-plus-circle me-1 fs-12"></i>
                  Ajouter un texte
                </button>
              </div>
            </div>
          </div>
          <div class="p-3">
            <div v-if="textes.length === 0" class="text-center text-muted py-3">
              Aucun texte. Cliquez sur "Ajouter un texte" pour en ajouter.
            </div>
            <div v-for="(texte, index) in textes" :key="texte.tempId || index">
              <div class="row mt-10" v-if="index === 0">
                <div class="col-md-4"><label class="d-block text-black fw-semibold mb-10">Titre <span class="text-danger">*</span></label></div>
                <div class="col-md-2"><label class="d-block text-black fw-semibold mb-10">Type</label></div>
                <div class="col-md-5"><label class="d-block text-black fw-semibold mb-10">Fichier <span class="text-danger">*</span></label></div>
                <div class="col-md-1"><label class="d-block text-black fw-semibold mb-10">Action</label></div>
              </div>
              <div class="row mb-10 align-items-start" :class="{ 'opacity-50': texte._toDelete }">
                <!-- Titre -->
                <div class="col-md-4">
                  <input type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Ex: Résumé du chapitre 1"
                    v-model="texte.titre"
                    :disabled="texte._toDelete" />
                </div>
                <!-- Type -->
                <div class="col-md-2">
                  <select class="form-select shadow-none fs-md-15 text-black"
                    v-model="texte.type" :disabled="texte._toDelete">
                    <option value="note">Note</option>
                    <option value="resume">Résumé</option>
                    <option value="definition">Définition</option>
                    <option value="exercice">Exercice</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <!-- Fichier -->
                <div class="col-md-5">
                  <div v-if="!texte._toDelete">
                    <input type="file" class="form-control shadow-none fs-md-15 text-black"
                      accept=".pdf,.doc,.docx,.txt"
                      @change="handleTexteFileUpload($event, index)" />
                    <small v-if="texte.fichierName" class="text-success d-block mt-1">
                      Nouveau : {{ texte.fichierName }}
                    </small>
                    <small v-if="texte.id && texte.lienActuel && !texte.fichier" class="text-muted d-block mt-1">
                      Fichier actuel : {{ texte.lienActuel }}
                    </small>
                  </div>
                  <span v-else class="badge bg-danger">Sera supprimé</span>
                </div>
                <!-- Statut -->
               <!-- <div class="col-md-1 pt-1">
                  <span class="badge" :class="texte.id ? 'bg-secondary' : 'bg-success'">
                    {{ texte.id ? 'Existant' : 'Nouveau' }}
                  </span>
                </div>-->
                <!-- Actions -->
                <div class="col-md-1 pt-1 d-flex justify-content-center align-items-center">
                  <button v-if="texte.id" class="btn btn-sm transition border-0 w-100"
                    :class="texte._toDelete ? 'btn-secondary' : 'btn-warning'"
                    type="button" @click="toggleDeleteTexte(index)"
                    :title="texte._toDelete ? 'Annuler la suppression' : 'Marquer pour suppression'">
                    <i :class="texte._toDelete ? 'fas fa-undo' : 'fas fa-trash'"></i>
                  </button>
                  <button v-else class="btn btn-sm btn-danger transition border-0 w-100"
                    type="button" @click="removeTexte(index)">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
              <hr v-if="index < textes.length - 1" class="my-2" />
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit" :disabled="isSubmitting">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Enregistrement...' : 'Sauvegarder les modifications' }}
              </button>
              <router-link to="/view-support" class="btn btn-danger transition border-0 lh-1 fw-medium">
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i> Annuler
              </router-link>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, onMounted } from "vue";
import { success, error } from "../../utils/utils";
import { useRouter, useRoute } from "vue-router";
import ApiService from "../../services/ApiService";

interface VideoItem {
  id?: number;            // présent si média existant
  tempId: string;         // identifiant temporaire unique pour le v-key
  titre: string;
  sourceType: 'url' | 'fichier';
  url: string;
  fichier: File | null;
  fichierName: string;
  lienActuel: string;     // lien stocké en base (pour affichage)
  description: string;
  _toDelete: boolean;     // true = à supprimer côté backend
  _isNew: boolean;        // true = pas encore en base
}

interface TexteItem {
  id?: number;
  tempId: string;
  titre: string;
  type: 'note' | 'resume' | 'definition' | 'exercice' | 'autre';
  fichier: File | null;
  fichierName: string;
  lienActuel: string;
  _toDelete: boolean;
  _isNew: boolean;
}

let _tempIdCounter = 0;
const genTempId = () => `tmp_${++_tempIdCounter}`;

export default defineComponent({
  name: "EditSupportCours",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const support = ref<any>(null);
    const isSubmitting = ref(false);

    const videos = reactive<VideoItem[]>([]);
    const textes = reactive<TexteItem[]>([]);

    // ─── Chargement du support et de ses médias existants ───────────────────
    const getSupport = async (id: string) => {
      try {
        const response = await ApiService.get(`/supports/${id}`);
        support.value = response.data.data;

        // Pré-remplir les collections à partir des médias existants
        const medias: any[] = support.value?.medias || [];
        medias.forEach((m: any) => {
          if (m.typeMedia === "video") {
            videos.push({
              id: m.id,
              tempId: genTempId(),
              titre: m.libelle || '',
              sourceType: m.type === 'url' ? 'url' : 'fichier',
              url: m.type === 'url' ? (m.lien || '') : '',
              fichier: null,
              fichierName: '',
              lienActuel: m.lien || '',
              description: m.description || '',
              _toDelete: false,
              _isNew: false,
            });
          } else if (m.typeMedia === "texte") {
            textes.push({
              id: m.id,
              tempId: genTempId(),
              titre: m.libelle || '',
              type: m.type || 'note',
              fichier: null,
              fichierName: '',
              lienActuel: m.lien || '',
              _toDelete: false,
              _isNew: false,
            });
          }
        });
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors du chargement du support");
      }
    };

    // ─── Vidéos : ajout / suppression / fichier ──────────────────────────────
    const addVideo = () => {
      videos.push({
        tempId: genTempId(),
        titre: '',
        sourceType: 'url',
        url: '',
        fichier: null,
        fichierName: '',
        lienActuel: '',
        description: '',
        _toDelete: false,
        _isNew: true,
      });
    };

    const removeVideo = (index: number) => {
      // Retire seulement les nouveaux (pas encore en base)
      videos.splice(index, 1);
    };

    const toggleDeleteVideo = (index: number) => {
      videos[index]._toDelete = !videos[index]._toDelete;
    };

    const onVideoFichierChange = (index: number, e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files?.[0] && videos[index]) {
        videos[index].fichier = input.files[0];
        videos[index].fichierName = input.files[0].name;
      }
    };

    // ─── Textes : ajout / suppression / fichier ──────────────────────────────
    const addTexte = () => {
      textes.push({
        tempId: genTempId(),
        titre: '',
        type: 'note',
        fichier: null,
        fichierName: '',
        lienActuel: '',
        _toDelete: false,
        _isNew: true,
      });
    };

    const removeTexte = (index: number) => {
      textes.splice(index, 1);
    };

    const toggleDeleteTexte = (index: number) => {
      textes[index]._toDelete = !textes[index]._toDelete;
    };

    const handleTexteFileUpload = (event: Event, index: number) => {
      const target = event.target as HTMLInputElement;
      if (target.files?.[0] && textes[index]) {
        textes[index].fichier = target.files[0];
        textes[index].fichierName = target.files[0].name;
      }
    };

    // ─── Soumission : PUT /medias/:id/cours ──────────────────────────────────
    const updateSupportMedias = async () => {
      isSubmitting.value = true;
      try {
        const formData = new FormData();

        // 1. IDs à supprimer
        const mediasToDelete = [
          ...videos.filter(v => v.id && v._toDelete).map(v => v.id),
          ...textes.filter(t => t.id && t._toDelete).map(t => t.id),
        ];
        if (mediasToDelete.length > 0) {
          formData.append('mediasToDelete', JSON.stringify(mediasToDelete));
        }

        // 2. Médias existants à mettre à jour (non marqués pour suppression)
        let updateVideoIndex = 0;
        let updateTexteIndex = 0;

        const mediasToUpdate: any[] = [];

        videos.filter(v => v.id && !v._toDelete).forEach((v) => {
          const entry: any = {
            id: v.id,
            libelle: v.titre?.trim() || v.lienActuel,
            description: v.description?.trim() || null,
            typeMedia: 'VIDEO',
            sourceType: v.sourceType,
            url: v.sourceType === 'url' ? v.url?.trim() : null,
          };
          mediasToUpdate.push(entry);

          if (v.fichier) {
            formData.append(`updateVideoFichier_${updateVideoIndex}`, v.fichier);
          }
          updateVideoIndex++;
        });

        textes.filter(t => t.id && !t._toDelete).forEach((t) => {
          const entry: any = {
            id: t.id,
            libelle: t.titre?.trim() || t.lienActuel,
            type: t.type,
            typeMedia: 'TEXTE',
          };
          mediasToUpdate.push(entry);

          if (t.fichier) {
            formData.append(`updateTexteFichier_${updateTexteIndex}`, t.fichier);
          }
          updateTexteIndex++;
        });

        if (mediasToUpdate.length > 0) {
          formData.append('mediasToUpdate', JSON.stringify(mediasToUpdate));
        }

        // 3. Nouveaux médias à créer
        let newVideoIndex = 0;
        let newTexteIndex = 0;

        const newVideos = videos.filter(v => !v.id && !v._toDelete);
        const newTextes = textes.filter(t => !t.id && !t._toDelete);

        const mediasToCreate: any[] = [];

        newVideos.forEach((v) => {
          mediasToCreate.push({
            titre: v.titre?.trim() || `Vidéo ${newVideoIndex + 1}`,
            typeMedia: 'VIDEO',
            sourceType: v.sourceType,
            url: v.sourceType === 'url' ? v.url?.trim() : null,
            description: v.description?.trim() || null,
            type: v.sourceType,
          });
          if (v.sourceType === 'fichier' && v.fichier) {
            formData.append(`newVideoFichier_${newVideoIndex}`, v.fichier);
          }
          newVideoIndex++;
        });

        newTextes.forEach((t) => {
          mediasToCreate.push({
            titre: t.titre?.trim() || `Texte ${newTexteIndex + 1}`,
            typeMedia: 'TEXTE',
            type: t.type,
            description: null,
          });
          if (t.fichier) {
            formData.append(`newTexteFichier_${newTexteIndex}`, t.fichier);
          }
          newTexteIndex++;
        });

        if (mediasToCreate.length > 0) {
          formData.append('mediasToCreate', JSON.stringify(mediasToCreate));
        }

        // Envoi vers le backend (PUT /medias/:id/cours)
        await ApiService.put(`/medias/${route.params.id}/cours`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        success("Médias mis à jour avec succès !");
        router.push('/liste-support');

      } catch (err: any) {
        console.error(err.response?.data || err);
        error(err.response?.data?.message || "Erreur lors de la mise à jour des médias");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(() => {
      const id = route.params.id as string | undefined;
      if (id) getSupport(id);
    });

    return {
      support,
      videos,
      textes,
      isSubmitting,
      addVideo,
      removeVideo,
      toggleDeleteVideo,
      onVideoFichierChange,
      addTexte,
      removeTexte,
      toggleDeleteTexte,
      handleTexteFileUpload,
      updateSupportMedias,
    };
  },
});
</script>

<style scoped>
.default-btn { background: #28a745; }
.default-btn:hover { background: #218838; }
.opacity-50 { opacity: 0.5; }
</style>